import { ArrowLeft } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import {
  getAppointmentEasternDateKey,
  getAppointmentEasternDayOffset,
  getDayHeadingLabel,
  isTaskReadinessComplete,
} from '../../helpers';
import HuddleVisitCard from '../HuddleVisitCard';
import { DayHeading, TopicBand } from '../Visit';
import styles from './PostVisitView.module.scss';

const LOOKBACK_OPTIONS = [3, 7, 14];
const DEFAULT_LOOKBACK_DAYS = 7;

const PostVisitView = ({
  postFilteredByDate,
  postVisitAppointmentTasksById,
  departmentLabelById = {},
  scopeLabel,
  todayKey,
  taskActions,
  onOpenTask,
  onBack,
}) => {
  const [lookbackDays, setLookbackDays] = useState(DEFAULT_LOOKBACK_DAYS);

  const dayGroups = useMemo(() => {
    const byDay = {};
    Object.values(postFilteredByDate)
      .flat()
      .forEach((appt) => {
        const dayOffset = getAppointmentEasternDayOffset(appt);
        if (dayOffset === null || dayOffset > 0 || dayOffset < -lookbackDays) return;
        const dayKey = getAppointmentEasternDateKey(appt);
        (byDay[dayKey] = byDay[dayKey] || []).push(appt);
      });

    return Object.keys(byDay)
      .sort((a, b) => b.localeCompare(a))
      .map((dayKey) => ({
        dayKey,
        appts: byDay[dayKey]
          .filter((appt) =>
            (postVisitAppointmentTasksById[appt.id] || []).some(
              (task) => !isTaskReadinessComplete(task),
            ),
          )
          .sort((a, b) => (b.time || '').localeCompare(a.time || '')),
      }))
      .filter((group) => group.appts.length > 0);
  }, [postFilteredByDate, postVisitAppointmentTasksById, lookbackDays]);

  const totalOpen = dayGroups.reduce(
    (sum, group) =>
      sum +
      group.appts.reduce(
        (visitSum, appt) =>
          visitSum +
          (postVisitAppointmentTasksById[appt.id] || []).filter(
            (task) => !isTaskReadinessComplete(task),
          ).length,
        0,
      ),
    0,
  );
  const totalVisits = dayGroups.reduce((sum, group) => sum + group.appts.length, 0);

  return (
    <div className={styles.root}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        <ArrowLeft size={13} aria-hidden="true" />
        Back to huddle
      </button>

      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <h1 className={styles.pageTitle}>Post-visit follow-up</h1>
          <p className={styles.summaryLine}>
            {totalOpen} open across {totalVisits} {totalVisits === 1 ? 'visit' : 'visits'} · last{' '}
            {lookbackDays} days · {scopeLabel}
          </p>
        </div>

        <div className={styles.presetGroup} role="group" aria-label="Look-back window">
          {LOOKBACK_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              aria-pressed={lookbackDays === days}
              className={`${styles.presetButton} ${
                lookbackDays === days ? styles.presetButtonActive : ''
              }`}
              onClick={() => setLookbackDays(days)}
            >
              {days} days
            </button>
          ))}
        </div>
      </div>

      <div className={styles.listCard}>
        <TopicBand density="band" label="Patients seen" meta={totalVisits} />
        <div className={styles.dayList}>
          {dayGroups.length === 0 && (
            <p className={styles.emptyState}>
              Nothing still open from the last {lookbackDays} days.
            </p>
          )}

          {dayGroups.map((group) => (
            <div key={group.dayKey} className={styles.dayGroup}>
              <DayHeading heading={getDayHeadingLabel(todayKey, group.dayKey)} />
              <div className={styles.dayRows}>
                {group.appts.map((appt) => (
                  <HuddleVisitCard
                    key={appt.id}
                    appt={appt}
                    departmentLabel={departmentLabelById[`${appt.departmentAthenaId ?? ''}`]}
                    tasks={postVisitAppointmentTasksById[appt.id] || []}
                    showNotes={false}
                    postVisit
                    markingTaskId={taskActions.markingTaskId}
                    assigningTaskId={taskActions.assigningTaskId}
                    onMarkComplete={taskActions.onMarkComplete}
                    onAssignToMe={taskActions.onAssignToMe}
                    onOpenTask={(task) =>
                      onOpenTask(task, appt.id, postVisitAppointmentTasksById[appt.id] || [])
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

PostVisitView.propTypes = {
  postFilteredByDate: PropTypes.object.isRequired,
  postVisitAppointmentTasksById: PropTypes.object,
  departmentLabelById: PropTypes.object,
  scopeLabel: PropTypes.string,
  todayKey: PropTypes.string.isRequired,
  taskActions: PropTypes.object.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default PostVisitView;
